// src/context/AuthContext.js - Updated with Email/Password Auth Support
import React, { useState, useEffect, createContext, useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';

// Create Auth Context
const AuthContext = createContext(null);

const normalizeUser = (user) => {
  if (!user) {
    return user;
  }

  return {
    ...user,
    staff_code: user.staff_code || user.employee_id || null,
  };
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [pendingRegistration, setPendingRegistration] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check for SSO Token in URL first
        const urlParams = new URLSearchParams(window.location.search);
        const ssoToken = urlParams.get('sso_token');

        if (ssoToken) {
          // Remove token from URL
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Set token and fetch user
          localStorage.setItem('sessionToken', ssoToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${ssoToken}`;
          
          try {
            const response = await axios.get(`${API_URL}/sso/me`);
            if (response.data.success) {
              const user = normalizeUser(response.data.user);
              localStorage.setItem('user', JSON.stringify(user));
              setCurrentUser(user);
              setPendingRegistration(null);
            }
          } catch (err) {
            console.error('Failed to fetch user with SSO token', err);
            // Fall through to regular auth check if failed
          }
        }

        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('sessionToken');
        const pendingUser = localStorage.getItem('pendingUser');

        if (pendingUser) {
          setPendingRegistration(normalizeUser(JSON.parse(pendingUser)));
          setCurrentUser(null);
        } else if (storedUser && storedToken) {
          const user = normalizeUser(JSON.parse(storedUser));
          localStorage.setItem('user', JSON.stringify(user));
          setCurrentUser(user);
          setPendingRegistration(null);
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        } else {
          setCurrentUser(null);
          setPendingRegistration(null);
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('sessionToken');
        localStorage.removeItem('pendingUser');
        delete axios.defaults.headers.common['Authorization'];
        setCurrentUser(null);
        setPendingRegistration(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Roles are enforced per-request on the backend, but a role change made in
  // SSO wouldn't otherwise reach an already-open tab until the user hits a
  // page that makes a fresh API call — someone sitting on /qc-queue after
  // losing the 'qc' role would stay there. Poll /sso/me while logged in so
  // PrivateRoute re-evaluates access and kicks them out proactively, not
  // just on their next click.
  useEffect(() => {
    if (!currentUser) return undefined;

    const ROLE_POLL_INTERVAL_MS = 30 * 1000;

    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API_URL}/sso/me`);
        if (response.data.success) {
          const user = normalizeUser(response.data.user);
          localStorage.setItem('user', JSON.stringify(user));
          setCurrentUser(user);
        }
      } catch (err) {
        // Token itself invalid/expired (401/403) — force logout. A transient
        // network error shouldn't log anyone out, so only act on those.
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          localStorage.removeItem('user');
          localStorage.removeItem('sessionToken');
          delete axios.defaults.headers.common['Authorization'];
          setCurrentUser(null);
        }
      }
    }, ROLE_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [currentUser?.id]);

  // Login user
  const login = (userData, token) => {
    if (!userData) return false;

    const normalizedUser = normalizeUser(userData);

    if (!normalizedUser.registration_complete) {
      // Store the pending registration
      localStorage.setItem('pendingUser', JSON.stringify(normalizedUser));
      setPendingRegistration(normalizedUser);
      setCurrentUser(null);
      return false; // Registration needed
    } else {
      // Store the complete user
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      setCurrentUser(normalizedUser);
      setPendingRegistration(null);
      localStorage.removeItem('pendingUser');

      // Set JWT token if provided (Telegram flow)
      if (token) {
        localStorage.setItem('sessionToken', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      return true; // Registration complete
    }
  };

  // Complete registration
  const completeRegistration = (userData) => {
    if (!userData) return;
    const normalizedUser = normalizeUser(userData);
    
    localStorage.setItem('user', JSON.stringify(normalizedUser));
    localStorage.removeItem('pendingUser');
    setCurrentUser(normalizedUser);
    setPendingRegistration(null);
  };

  // Login with email/password
  const loginWithEmail = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password
      });

      if (response.data.success) {
        const { token } = response.data;
        const user = normalizeUser(response.data.user);

        // Store user and token
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('sessionToken', token);

        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        setCurrentUser(user);
        setPendingRegistration(null);

        return true;
      }

      return false;
    } catch (error) {
      console.error('Email login error:', error);
      const errorMessage = error.response?.data?.message || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Logout user
  const logout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('pendingUser');
    delete axios.defaults.headers.common['Authorization'];
    setCurrentUser(null);
    setPendingRegistration(null);
  };

  const value = {
    currentUser,
    pendingRegistration,
    loading,
    login,
    loginWithEmail,
    completeRegistration,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook for using Auth Context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Private Route Component
// allowedRoles: if given, currentUser.roles must include at least one of
// them, mirroring the backend's requireRole() — same as the backend, this
// does NOT fail open, so an account with no roles assigned yet is blocked
// same as one with the wrong role.
export const PrivateRoute = ({ children, allowedRoles }) => {
  const { currentUser, loading, pendingRegistration } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (pendingRegistration) {
    return <Navigate to="/register" state={{ from: location }} replace />;
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && allowedRoles.length > 0) {
    const userRoles = currentUser.roles || [];
    const hasAccess = userRoles.some(r => allowedRoles.includes(r));
    if (!hasAccess) {
      toast.error('บัญชีของคุณไม่มีสิทธิ์เข้าถึงหน้านี้');
      return <Navigate to="/" replace />;
    }
  }

  return children;
};

export default AuthContext;
